/**
 * frameBudgetReporting — FrameBudgetMonitor wired to the live client
 * (plan 13 §8). Owned by FreezeSentinel, which feeds it from its rAF loop.
 *
 * Delivery: `room.send('netobs:frameBudget', event)`; the server validates it
 * (src/server/validation/frameBudgetSchema.ts) and records the `system`
 * DomainLog event `frame_budget_degraded`. No room yet (boot) ⇒ the report is
 * only logged locally — a slow boot is the freeze sentinel's business.
 */

import { classifyFocus } from '../../shared/diagnostics/FreezeEventTypes';
import type { FrameBudgetEventWire } from '../../shared/diagnostics/FrameBudgetTypes';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { gameLogger } from '../../utils/GameLogger';
import { FrameBudgetMonitor } from './FrameBudgetMonitor';
import { FramePhaseBudget } from './FramePhaseBudget';
import { drainReactCommits } from './reactCommitBudget';
import { readSceneCounts } from './sceneCounts';
import { bootPathname } from '../../config/boot/bootUrl';

interface RoomLike {
  send(type: string, payload: unknown): void;
  state?: { players?: { size?: number } };
}

function currentRoom(): RoomLike | null {
  const room = (globalThis as { nm?: { getRoom?: () => unknown } }).nm?.getRoom?.();
  return room && typeof (room as RoomLike).send === 'function' ? room as RoomLike : null;
}

function readHeapMB(): number | null {
  const used = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize;
  return typeof used === 'number' && Number.isFinite(used) ? Math.round(used / 1048576) : null;
}

function collectContext(): Pick<FrameBudgetEventWire,
  'route' | 'levelId' | 'focusClass' | 'mobCount' | 'playerCount' | 'scene' | 'heapUsedMB' | 'domNodes'> {
  const registry = ServiceRegistry.getInstance();
  const mapSys = registry.get('MapSystem', { optional: true }) as
    | { getCurrentLevel?(): { id: string } | null }
    | undefined;
  const mobSys = registry.get('MobSystem', { optional: true }) as
    | { getManager?(): { getAllMobs?(): readonly unknown[] } | null }
    | undefined;
  const levelId = mapSys?.getCurrentLevel?.()?.id;
  const mobs = mobSys?.getManager?.()?.getAllMobs?.();
  const players = currentRoom()?.state?.players?.size;
  const hasDoc = typeof document !== 'undefined';
  return {
    route: bootPathname(),
    ...(levelId ? { levelId } : {}),
    focusClass: classifyFocus({
      documentHidden: hasDoc ? document.hidden : undefined,
      hasFocus: hasDoc ? document.hasFocus() : undefined,
    }),
    mobCount: mobs ? mobs.length : null,
    playerCount: typeof players === 'number' ? players : null,
    scene: readSceneCounts(),
    heapUsedMB: readHeapMB(),
    domNodes: hasDoc ? document.getElementsByTagName('*').length : null,
  };
}

function send(event: FrameBudgetEventWire): void {
  const f = event.frames;
  const top = event.phaseBudgetMs.map((p) => `${p.phase} ${p.msPerSec}ms/s`).join(', ');
  gameLogger.warn('perf', `[frame-budget] ${event.levelId ?? '?'}: ${f.fps} fps, p95 ${f.p95Ms} ms, `
    + `${f.over50} frames > 50 ms; ${top}; ${event.longFrames} long frames`, event);
  currentRoom()?.send('netobs:frameBudget', event);
}

export function createFrameBudgetMonitor(): FrameBudgetMonitor {
  return new FrameBudgetMonitor({
    wallNow: () => Date.now(),
    drainPhases: (windowMs) => FramePhaseBudget.drain(windowMs),
    drainReactCommits: () => drainReactCommits(),
    collectContext,
    send,
  });
}
